import { useEffect, useState } from "react";
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../api/firebase/references";
import { HealthCareFacilityModel } from "../data-model/healthFacility";

/**
 * Represents the state returned by the useHealthCareFacility hook.
 * @interface UseHealthCareFacilityData
 * @property {HealthCareFacilityModel} [data] - The fetched health care facility data, undefined if not yet loaded or on error.
 * @property {boolean} loading - Indicates whether the facility data is currently being fetched.
 * @property {string | null} error - Error message if the fetch failed, null otherwise.
 */
export interface UseHealthCareFacilityData {
  data?: HealthCareFacilityModel;
  loading: boolean;
  error: string | null;
}

/**
 * Custom React hook to fetch and manage a single health care facility's data from Firestore.
 *
 * @param {string} healthCareFacilityId - The unique identifier of the health care facility to fetch.
 * @returns {{
 *   healthCareFacilityData: UseHealthCareFacilityData,
 *   fetchHealthCareFacility: () => Promise<void>
 * }} An object containing:
 *   - `healthCareFacilityData` - The current state including data, loading status, and error.
 *   - `fetchHealthCareFacility` - Function to manually refetch the facility data.
 *
 * @example
 * const { healthCareFacilityData, fetchHealthCareFacility } = useHealthCareFacility("facility-123");
 *
 * if (healthCareFacilityData.loading) {
 *   return <Spinner />;
 * }
 *
 * if (healthCareFacilityData.error) {
 *   return <Error message={healthCareFacilityData.error} />;
 * }
 *
 * return <FacilityDetails facility={healthCareFacilityData.data} />;
 */
export const useHealthCareFacility = (healthCareFacilityId: string) => {
  const [healthCareFacilityData, setHealthCareFacilityData] =
    useState<UseHealthCareFacilityData>({
      data: undefined,
      loading: false,
      error: null,
    });

  const fetchHealthCareFacility = async () => {
    setHealthCareFacilityData((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));
    try {
      const healthCareFacility =
        await FirestoreService.getDocument<HealthCareFacilityModel>(
          FirestoreDocumentReference.healthFacility(healthCareFacilityId),
        );
      setHealthCareFacilityData({
        data: healthCareFacility,
        loading: false,
        error: null,
      });
    } catch (error) {
      setHealthCareFacilityData((prev) => ({
        ...prev,
        error: error as string,
        data: undefined,
      }));
    } finally {
      setHealthCareFacilityData((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchHealthCareFacility();
  }, []);

  return {
    healthCareFacilityData,
    fetchHealthCareFacility,
  };
};
